/**
 * BusinessLogic - Comprehensive arbitrage calculations and betting logic utilities
 * 
 * Features:
 * - Margin calculation utilities
 * - Stake distribution calculation helpers
 * - Profit calculation functions
 * - Odds conversion utilities (Decimal, Fractional, American)
 * - Risk assessment helpers
 * - Betting scenario analysis tools
 */

/**
 * Default limits for betting calculations
 */
export const BETTING_LIMITS = {
  MIN_STAKE: 0.01,
  MAX_STAKE: 100000,
  MIN_ODDS: 1.01,
  MAX_ODDS: 1000,
  MIN_MARGIN: 0.01, // 0.01%
  MAX_MARGIN: 100
};

/**
 * Odds format types
 */
export const ODDS_FORMAT = {
  DECIMAL: 'decimal',
  FRACTIONAL: 'fractional',
  AMERICAN: 'american'
};

/**
 * Risk levels
 */
export const RISK_LEVELS = {
  VERY_LOW: 'very_low',
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  VERY_HIGH: 'very_high'
};

/**
 * BusinessLogic class with comprehensive betting and arbitrage utilities
 */
class BusinessLogic {
  /**
   * Calculate arbitrage margin from odds
   * @param {Array<number>} odds - Array of decimal odds
   * @returns {Object} Margin calculation result
   */
  static calculateMargin(odds) {
    try {
      if (!odds || !Array.isArray(odds) || odds.length === 0) {
        return {
          isValid: false,
          margin: 0,
          marginPercentage: 0,
          totalImpliedProbability: 0,
          error: 'Invalid odds array'
        };
      }

      // Filter valid odds (must be > 1)
      const validOdds = odds
        .map(odd => typeof odd === 'object' ? parseFloat(odd.value || odd) : parseFloat(odd))
        .filter(odd => !isNaN(odd) && odd > 1);

      if (validOdds.length === 0) {
        return {
          isValid: false,
          margin: 0,
          marginPercentage: 0,
          totalImpliedProbability: 0,
          error: 'No valid odds found'
        };
      }

      // Calculate total implied probability
      const totalImpliedProbability = validOdds.reduce((sum, odd) => {
        return sum + (1 / odd);
      }, 0);

      // Calculate arbitrage margin
      const margin = 1 - totalImpliedProbability;
      const marginPercentage = margin * 100;

      return {
        isValid: margin > 0,
        margin: this.roundToPrecision(margin, 6),
        marginPercentage: this.roundToPrecision(marginPercentage, 4),
        totalImpliedProbability: this.roundToPrecision(totalImpliedProbability, 6),
        validOddsCount: validOdds.length,
        error: margin <= 0 ? 'No arbitrage opportunity (margin <= 0)' : null
      };
    } catch (error) {
      return {
        isValid: false,
        margin: 0,
        marginPercentage: 0,
        totalImpliedProbability: 0,
        error: `Calculation error: ${error.message}`
      };
    }
  }

  /**
   * Calculate optimal stake distribution for arbitrage
   * @param {Array} odds - Array of odds objects with bookmaker/outcome info
   * @param {number} totalStake - Total stake amount
   * @param {Object} options - Calculation options
   * @returns {Object} Stake distribution result
   */
  static calculateStakeDistribution(odds, totalStake, options = {}) {
    try {
      const {
        minStakePerBookmaker = BETTING_LIMITS.MIN_STAKE,
        maxStakePerBookmaker = BETTING_LIMITS.MAX_STAKE,
        roundingPrecision = 2,
        allowNegativeStakes = false
      } = options;

      if (!odds || !Array.isArray(odds) || odds.length === 0) {
        return {
          isValid: false,
          distribution: [],
          totalStake: 0,
          guaranteedProfit: 0,
          error: 'Invalid odds array'
        };
      }

      const totalStakeAmount = parseFloat(totalStake);
      if (isNaN(totalStakeAmount) || totalStakeAmount <= 0) {
        return {
          isValid: false,
          distribution: [],
          totalStake: 0,
          guaranteedProfit: 0,
          error: 'Invalid total stake amount'
        };
      }

      // Extract and validate odds
      const validOdds = odds.map((odd, index) => {
        const value = typeof odd === 'object' ? parseFloat(odd.value || odd) : parseFloat(odd);
        const bookmaker = odd.bookmaker || `Bookmaker ${index + 1}`;
        const outcome = odd.outcome || odd.type || `Outcome ${index + 1}`;
        
        return {
          index,
          value: isNaN(value) || value <= 1 ? null : value,
          bookmaker,
          outcome,
          original: odd
        };
      }).filter(odd => odd.value !== null);

      if (validOdds.length === 0) {
        return {
          isValid: false,
          distribution: [],
          totalStake: 0,
          guaranteedProfit: 0,
          error: 'No valid odds found'
        };
      }

      // Calculate total implied probability
      const totalImpliedProbability = validOdds.reduce((sum, odd) => {
        return sum + (1 / odd.value);
      }, 0);

      // Still compute stake distribution when there is no positive arbitrage
      // (implied probability >= 1) so the UI can show stakes and a negative ROI.
      // Calculate optimal stakes
      const distribution = validOdds.map(odd => {
        // Calculate optimal stake
        const optimalStake = totalStakeAmount / (odd.value * totalImpliedProbability);
        
        // Apply constraints
        let finalStake = optimalStake;
        
        if (!allowNegativeStakes && finalStake < 0) {
          finalStake = 0;
        }
        
        if (finalStake < minStakePerBookmaker) {
          finalStake = minStakePerBookmaker;
        }
        
        if (finalStake > maxStakePerBookmaker) {
          finalStake = maxStakePerBookmaker;
        }
        
        // Round to specified precision
        const roundedStake = this.roundToPrecision(finalStake, roundingPrecision);
        
        // Calculate potential return and profit
        const potentialReturn = roundedStake * odd.value;
        const profit = potentialReturn - totalStakeAmount;
        const percentage = totalStakeAmount > 0 ? (roundedStake / totalStakeAmount) * 100 : 0;
        
        return {
          index: odd.index,
          bookmaker: odd.bookmaker,
          outcome: odd.outcome,
          odds: odd.value,
          stake: roundedStake,
          percentage: this.roundToPrecision(percentage, 2),
          potentialReturn: this.roundToPrecision(potentialReturn, roundingPrecision),
          profit: this.roundToPrecision(profit, roundingPrecision),
          isValid: roundedStake >= minStakePerBookmaker && roundedStake <= maxStakePerBookmaker
        };
      });

      // Calculate guaranteed profit
      const guaranteedProfit = this.roundToPrecision(
        (totalStakeAmount / totalImpliedProbability) - totalStakeAmount,
        roundingPrecision
      );
      
      // Calculate actual total stake (may differ due to constraints)
      const actualTotalStake = distribution.reduce((sum, item) => sum + item.stake, 0);
      
      // Check if all stakes are valid
      const allValid = distribution.every(item => item.isValid);
      
      // Calculate margin
      const margin = this.calculateMargin(validOdds.map(odd => odd.value));
      
      return {
        isValid: allValid,
        distribution,
        totalStake: actualTotalStake,
        guaranteedProfit,
        guaranteedProfitPercentage: this.roundToPrecision((guaranteedProfit / totalStakeAmount) * 100, 4),
        arbitrageMargin: margin.marginPercentage,
        totalImpliedProbability: this.roundToPrecision(totalImpliedProbability, 6),
        error: allValid ? null : 'Some stakes exceed constraints',
      };
    } catch (error) {
      return {
        isValid: false,
        distribution: [],
        totalStake: 0,
        guaranteedProfit: 0,
        error: `Calculation error: ${error.message}`
      };
    }
  }

  /**
   * Calculate stake distribution when one position stake is fixed (surebet.com Fix mode).
   * Other stakes are derived from equal return: stake_i = (fixedStake × odd_fixed) / odd_i.
   * @param {Array} odds - Array of odds objects with bookmaker/outcome info
   * @param {number} fixedIndex - Index of the fixed stake position
   * @param {number} fixedStake - Fixed stake amount for the locked position
   * @param {Object} options - Calculation options
   * @returns {Object} Stake distribution result
   */
  static calculateStakeDistributionFromFixedStake(odds, fixedIndex, fixedStake, options = {}) {
    try {
      const {
        minStakePerBookmaker = BETTING_LIMITS.MIN_STAKE,
        maxStakePerBookmaker = BETTING_LIMITS.MAX_STAKE,
        roundingPrecision = 2,
      } = options;

      if (!odds || !Array.isArray(odds) || odds.length === 0) {
        return {
          isValid: false,
          distribution: [],
          totalStake: 0,
          guaranteedProfit: 0,
          error: 'Invalid odds array',
        };
      }

      const fixedStakeAmount = parseFloat(fixedStake);
      if (isNaN(fixedStakeAmount) || fixedStakeAmount <= 0) {
        return {
          isValid: false,
          distribution: [],
          totalStake: 0,
          guaranteedProfit: 0,
          error: 'Invalid fixed stake amount',
        };
      }

      const parsedFixedIndex = parseInt(fixedIndex, 10);
      if (isNaN(parsedFixedIndex) || parsedFixedIndex < 0 || parsedFixedIndex >= odds.length) {
        return {
          isValid: false,
          distribution: [],
          totalStake: 0,
          guaranteedProfit: 0,
          error: 'Invalid fixed stake index',
        };
      }

      const validOdds = odds.map((odd, index) => {
        const value = typeof odd === 'object' ? parseFloat(odd.value || odd) : parseFloat(odd);
        const bookmaker = odd.bookmaker || `Bookmaker ${index + 1}`;
        const outcome = odd.outcome || odd.type || `Outcome ${index + 1}`;

        return {
          index,
          value: isNaN(value) || value <= 1 ? null : value,
          bookmaker,
          outcome,
          original: odd,
        };
      }).filter((odd) => odd.value !== null);

      const fixedOdd = validOdds.find((odd) => odd.index === parsedFixedIndex);
      if (!fixedOdd) {
        return {
          isValid: false,
          distribution: [],
          totalStake: 0,
          guaranteedProfit: 0,
          error: 'Fixed position has invalid odds',
        };
      }

      const totalImpliedProbability = validOdds.reduce((sum, odd) => sum + (1 / odd.value), 0);

      // Keep distributing stakes even without positive arbitrage so ROI can go negative.
      const targetReturn = fixedStakeAmount * fixedOdd.value;

      const distribution = validOdds.map((odd) => {
        const rawStake = odd.index === parsedFixedIndex
          ? fixedStakeAmount
          : targetReturn / odd.value;

        let finalStake = rawStake;

        if (finalStake < minStakePerBookmaker) {
          finalStake = minStakePerBookmaker;
        }

        if (finalStake > maxStakePerBookmaker) {
          finalStake = maxStakePerBookmaker;
        }

        const roundedStake = this.roundToPrecision(finalStake, roundingPrecision);
        const potentialReturn = roundedStake * odd.value;

        return {
          index: odd.index,
          bookmaker: odd.bookmaker,
          outcome: odd.outcome,
          odds: odd.value,
          stake: roundedStake,
          potentialReturn: this.roundToPrecision(potentialReturn, roundingPrecision),
          profit: 0,
          isValid: roundedStake >= minStakePerBookmaker && roundedStake <= maxStakePerBookmaker,
          isFixed: odd.index === parsedFixedIndex,
        };
      });

      const actualTotalStake = this.roundToPrecision(
        distribution.reduce((sum, item) => sum + item.stake, 0),
        roundingPrecision,
      );

      const guaranteedProfit = this.roundToPrecision(targetReturn - actualTotalStake, roundingPrecision);

      const distributionWithProfit = distribution.map((item) => ({
        ...item,
        profit: this.roundToPrecision(item.potentialReturn - actualTotalStake, roundingPrecision),
        percentage: actualTotalStake > 0
          ? this.roundToPrecision((item.stake / actualTotalStake) * 100, 2)
          : 0,
      }));

      const allValid = distributionWithProfit.every((item) => item.isValid);
      const margin = this.calculateMargin(validOdds.map((odd) => odd.value));

      return {
        isValid: allValid,
        distribution: distributionWithProfit,
        totalStake: actualTotalStake,
        guaranteedProfit,
        guaranteedProfitPercentage: actualTotalStake > 0
          ? this.roundToPrecision((guaranteedProfit / actualTotalStake) * 100, 4)
          : 0,
        arbitrageMargin: margin.marginPercentage,
        totalImpliedProbability: this.roundToPrecision(totalImpliedProbability, 6),
        fixedStakeIndex: parsedFixedIndex,
        error: allValid ? null : 'Some stakes exceed constraints',
      };
    } catch (error) {
      return {
        isValid: false,
        distribution: [],
        totalStake: 0,
        guaranteedProfit: 0,
        error: `Calculation error: ${error.message}`,
      };
    }
  }

  /**
   * Calculate stake distribution with anchored total and manual position stakes.
   * Remaining positions are either remainder (when N-1 manual) or optimal split of leftover budget.
   * Guaranteed profit uses the minimum profit across outcomes.
   * @param {Array} odds - Array of odds objects with bookmaker/outcome info
   * @param {number} anchoredTotal - Fixed total stake amount
   * @param {Object} manualStakes - Manual stakes keyed by position index
   * @param {Object} options - Calculation options
   * @returns {Object} Stake distribution result
   */
  static calculateStakeDistributionFromAnchoredTotal(odds, anchoredTotal, manualStakes = {}, options = {}) {
    try {
      const {
        minStakePerBookmaker = BETTING_LIMITS.MIN_STAKE,
        maxStakePerBookmaker = BETTING_LIMITS.MAX_STAKE,
        roundingPrecision = 2,
      } = options;

      if (!odds || !Array.isArray(odds) || odds.length === 0) {
        return {
          isValid: false,
          distribution: [],
          totalStake: 0,
          guaranteedProfit: 0,
          error: 'Invalid odds array',
        };
      }

      const anchoredTotalAmount = parseFloat(anchoredTotal);
      if (isNaN(anchoredTotalAmount) || anchoredTotalAmount <= 0) {
        return {
          isValid: false,
          distribution: [],
          totalStake: 0,
          guaranteedProfit: 0,
          error: 'Invalid anchored total amount',
        };
      }

      const validOdds = odds.map((odd, index) => {
        const value = typeof odd === 'object' ? parseFloat(odd.value || odd) : parseFloat(odd);
        const bookmaker = odd.bookmaker || `Bookmaker ${index + 1}`;
        const outcome = odd.outcome || odd.type || `Outcome ${index + 1}`;

        return {
          index,
          value: isNaN(value) || value <= 1 ? null : value,
          bookmaker,
          outcome,
          original: odd,
        };
      }).filter((odd) => odd.value !== null);

      if (validOdds.length === 0) {
        return {
          isValid: false,
          distribution: [],
          totalStake: 0,
          guaranteedProfit: 0,
          error: 'No valid odds found',
        };
      }

      const normalizedManualStakes = {};
      Object.entries(manualStakes || {}).forEach(([rawIndex, rawValue]) => {
        const parsedIndex = parseInt(rawIndex, 10);
        const parsedValue = parseFloat(rawValue);
        if (
          !isNaN(parsedIndex)
          && parsedIndex >= 0
          && parsedIndex < odds.length
          && !isNaN(parsedValue)
          && parsedValue > 0
        ) {
          normalizedManualStakes[parsedIndex] = parsedValue;
        }
      });

      const manualIndices = Object.keys(normalizedManualStakes).map((key) => parseInt(key, 10));
      if (manualIndices.length === 0) {
        return {
          isValid: false,
          distribution: [],
          totalStake: 0,
          guaranteedProfit: 0,
          error: 'At least one manual stake is required',
        };
      }

      const manualSum = manualIndices.reduce(
        (sum, index) => sum + normalizedManualStakes[index],
        0,
      );

      const allPositionsManual = manualIndices.length === validOdds.length;

      if (manualSum > anchoredTotalAmount + 0.01 && !allPositionsManual) {
        return {
          isValid: false,
          distribution: [],
          totalStake: 0,
          guaranteedProfit: 0,
          error: 'Manual stakes exceed anchored total',
        };
      }

      const freeOdds = validOdds.filter((odd) => !manualIndices.includes(odd.index));
      const remainingBudget = anchoredTotalAmount - manualSum;
      const stakeByIndex = { ...normalizedManualStakes };
      let derivedStakeIndex = null;

      if (freeOdds.length === 1 && remainingBudget > 0) {
        derivedStakeIndex = freeOdds[0].index;
        stakeByIndex[derivedStakeIndex] = remainingBudget;
      } else if (freeOdds.length > 1 && remainingBudget > 0) {
        const subResult = this.calculateStakeDistribution(freeOdds, remainingBudget, options);
        if (!subResult.isValid || subResult.distribution.length === 0) {
          return {
            isValid: false,
            distribution: [],
            totalStake: 0,
            guaranteedProfit: 0,
            error: subResult.error || 'Unable to distribute remaining budget',
          };
        }

        subResult.distribution.forEach((item) => {
          stakeByIndex[item.index] = item.stake;
        });
      }

      const distributionStakes = validOdds.map((odd) => {
        let finalStake = stakeByIndex[odd.index];

        if (finalStake === undefined || isNaN(finalStake)) {
          return null;
        }

        if (finalStake < minStakePerBookmaker) {
          finalStake = minStakePerBookmaker;
        }

        if (finalStake > maxStakePerBookmaker) {
          finalStake = maxStakePerBookmaker;
        }

        const roundedStake = this.roundToPrecision(finalStake, roundingPrecision);

        return {
          index: odd.index,
          bookmaker: odd.bookmaker,
          outcome: odd.outcome,
          odds: odd.value,
          stake: roundedStake,
          isManual: manualIndices.includes(odd.index),
          isDerived: odd.index === derivedStakeIndex,
          isValid: roundedStake >= minStakePerBookmaker && roundedStake <= maxStakePerBookmaker,
        };
      }).filter(Boolean);

      if (distributionStakes.length !== validOdds.length) {
        return {
          isValid: false,
          distribution: [],
          totalStake: 0,
          guaranteedProfit: 0,
          error: 'Incomplete stake distribution',
        };
      }

      const actualTotalStake = this.roundToPrecision(
        distributionStakes.reduce((sum, item) => sum + item.stake, 0),
        roundingPrecision,
      );

      const profitBasis = allPositionsManual ? actualTotalStake : anchoredTotalAmount;
      const percentageBasis = profitBasis > 0 ? profitBasis : anchoredTotalAmount;

      const distribution = distributionStakes.map((item) => {
        const potentialReturn = item.stake * item.odds;
        const profit = this.roundToPrecision(potentialReturn - profitBasis, roundingPrecision);

        return {
          ...item,
          potentialReturn: this.roundToPrecision(potentialReturn, roundingPrecision),
          profit,
          percentage: percentageBasis > 0
            ? this.roundToPrecision((item.stake / percentageBasis) * 100, 2)
            : 0,
        };
      });

      const profits = distribution.map((item) => item.profit);
      const minProfit = this.roundToPrecision(Math.min(...profits), roundingPrecision);
      const allValid = distribution.every((item) => item.isValid);
      const sumMatchesTotal = Math.abs(actualTotalStake - anchoredTotalAmount) <= 0.01;
      const margin = this.calculateMargin(validOdds.map((odd) => odd.value));
      const isDistributionValid = allValid && sumMatchesTotal;
      const roiBasis = allPositionsManual ? actualTotalStake : anchoredTotalAmount;

      let guaranteedProfit = minProfit;
      let guaranteedProfitPercentage = roiBasis > 0
        ? this.roundToPrecision((minProfit / roiBasis) * 100, 4)
        : 0;

      if (margin.isValid && margin.totalImpliedProbability > 0 && roiBasis > 0) {
        guaranteedProfit = this.roundToPrecision(
          roiBasis / margin.totalImpliedProbability - roiBasis,
          roundingPrecision,
        );
        guaranteedProfitPercentage = this.roundToPrecision(
          (guaranteedProfit / roiBasis) * 100,
          4,
        );
      }

      return {
        isValid: isDistributionValid,
        distribution,
        totalStake: anchoredTotalAmount,
        actualTotalStake,
        profitBasis,
        guaranteedProfit,
        guaranteedProfitPercentage,
        arbitrageMargin: margin.marginPercentage,
        derivedStakeIndex,
        manualStakeIndices: manualIndices,
        allPositionsManual,
        error: !sumMatchesTotal
          ? (actualTotalStake > anchoredTotalAmount + 0.01
            ? 'Manual stakes exceed anchored total'
            : 'Manual stakes must equal anchored total')
          : (allValid ? null : 'Some stakes exceed constraints'),
      };
    } catch (error) {
      return {
        isValid: false,
        distribution: [],
        totalStake: 0,
        guaranteedProfit: 0,
        error: `Calculation error: ${error.message}`,
      };
    }
  }

  /**
   * Calculate profit for each outcome scenario
   * @param {Array} stakeDistribution - Array of stake distribution data
   * @param {number} totalStake - Total stake amount
   * @returns {Array} Profit scenarios for each outcome
   */
  static calculateProfitScenarios(stakeDistribution, totalStake) {
    try {
      if (!stakeDistribution || !Array.isArray(stakeDistribution) || stakeDistribution.length === 0) {
        return [];
      }

      const totalStakeAmount = parseFloat(totalStake);
      if (isNaN(totalStakeAmount) || totalStakeAmount <= 0) {
        return [];
      }

      return stakeDistribution.map((stake, index) => {
        const stakeAmount = parseFloat(stake.stake || stake.stakeAmount || 0);
        const oddsValue = parseFloat(stake.odds || stake.oddsValue || 0);
        
        if (isNaN(stakeAmount) || isNaN(oddsValue) || stakeAmount <= 0 || oddsValue <= 1) {
          return {
            index,
            outcome: stake.outcome || `Outcome ${index + 1}`,
            bookmaker: stake.bookmaker || `Bookmaker ${index + 1}`,
            stakeAmount: 0,
            oddsValue: 0,
            winningReturn: 0,
            profit: -totalStakeAmount,
            profitPercentage: -100,
            roi: 0,
            isValid: false
          };
        }

        const winningReturn = stakeAmount * oddsValue;
        const profit = winningReturn - totalStakeAmount;
        const profitPercentage = (profit / totalStakeAmount) * 100;
        const roi = stakeAmount > 0 ? (profit / stakeAmount) * 100 : 0;

        return {
          index,
          outcome: stake.outcome || `Outcome ${index + 1}`,
          bookmaker: stake.bookmaker || `Bookmaker ${index + 1}`,
          stakeAmount,
          oddsValue,
          winningReturn: this.roundToPrecision(winningReturn, 2),
          profit: this.roundToPrecision(profit, 2),
          profitPercentage: this.roundToPrecision(profitPercentage, 2),
          roi: this.roundToPrecision(roi, 2),
          isValid: true
        };
      });
    } catch (error) {
      console.error('Error calculating profit scenarios:', error);
      return [];
    }
  }

  /**
   * Calculate ROI (Return on Investment)
   * @param {number} profit - Profit amount
   * @param {number} investment - Investment amount
   * @returns {number} ROI percentage
   */
  static calculateROI(profit, investment) {
    try {
      const profitAmount = parseFloat(profit);
      const investmentAmount = parseFloat(investment);
      
      if (isNaN(profitAmount) || isNaN(investmentAmount) || investmentAmount === 0) {
        return 0;
      }
      
      return this.roundToPrecision((profitAmount / investmentAmount) * 100, 2);
    } catch (error) {
      console.error('Error calculating ROI:', error);
      return 0;
    }
  }

  /**
   * Convert decimal odds to fractional
   * @param {number} decimal - Decimal odds
   * @returns {Object} Fractional odds with numerator and denominator
   */
  static decimalToFractional(decimal) {
    try {
      const num = parseFloat(decimal);
      
      if (isNaN(num) || num <= 1) {
        return {
          isValid: false,
          numerator: 0,
          denominator: 1,
          fraction: '0/1',
          error: 'Invalid decimal odds'
        };
      }
      
      // Convert to fractional (decimal - 1)
      const fractional = num - 1;
      
      // Find common fraction
      const tolerance = 0.0001;
      let denominator = 1;
      let numerator = Math.round(fractional * denominator);
      
      while (Math.abs(fractional - numerator / denominator) > tolerance && denominator < 1000) {
        denominator++;
        numerator = Math.round(fractional * denominator);
      }
      
      // Simplify fraction
      const gcd = this.gcd(numerator, denominator);
      const simplifiedNumerator = numerator / gcd;
      const simplifiedDenominator = denominator / gcd;
      
      return {
        isValid: true,
        numerator: simplifiedNumerator,
        denominator: simplifiedDenominator,
        fraction: `${simplifiedNumerator}/${simplifiedDenominator}`,
        decimal: num,
        error: null
      };
    } catch (error) {
      return {
        isValid: false,
        numerator: 0,
        denominator: 1,
        fraction: '0/1',
        error: `Conversion error: ${error.message}`
      };
    }
  }

  /**
   * Convert fractional odds to decimal
   * @param {number} numerator - Fraction numerator
   * @param {number} denominator - Fraction denominator
   * @returns {number} Decimal odds
   */
  static fractionalToDecimal(numerator, denominator) {
    try {
      const num = parseFloat(numerator);
      const den = parseFloat(denominator);
      
      if (isNaN(num) || isNaN(den) || den === 0) {
        return 0;
      }
      
      return this.roundToPrecision((num / den) + 1, 4);
    } catch (error) {
      console.error('Error converting fractional to decimal:', error);
      return 0;
    }
  }

  /**
   * Convert decimal odds to American
   * @param {number} decimal - Decimal odds
   * @returns {Object} American odds
   */
  static decimalToAmerican(decimal) {
    try {
      const num = parseFloat(decimal);
      
      if (isNaN(num) || num <= 1) {
        return {
          isValid: false,
          american: 0,
          formatted: '+0',
          error: 'Invalid decimal odds'
        };
      }
      
      let american;
      
      if (num < 2) {
        // Favorite (negative)
        american = Math.round(-100 / (num - 1));
      } else {
        // Underdog (positive)
        american = Math.round((num - 1) * 100);
      }
      
      return {
        isValid: true,
        american,
        formatted: american >= 0 ? `+${american}` : `${american}`,
        decimal: num,
        error: null
      };
    } catch (error) {
      return {
        isValid: false,
        american: 0,
        formatted: '+0',
        error: `Conversion error: ${error.message}`
      };
    }
  }

  /**
   * Convert American odds to decimal
   * @param {number} american - American odds
   * @returns {number} Decimal odds
   */
  static americanToDecimal(american) {
    try {
      const num = parseFloat(american);
      
      if (isNaN(num)) {
        return 0;
      }
      
      let decimal;
      
      if (num > 0) {
        // Positive (underdog)
        decimal = (num / 100) + 1;
      } else {
        // Negative (favorite)
        decimal = (-100 / num) + 1;
      }
      
      return this.roundToPrecision(decimal, 4);
    } catch (error) {
      console.error('Error converting American to decimal:', error);
      return 0;
    }
  }

  /**
   * Calculate greatest common divisor
   * @param {number} a - First number
   * @param {number} b - Second number
   * @returns {number} GCD
   */
  static gcd(a, b) {
    a = Math.abs(a);
    b = Math.abs(b);
    
    while (b !== 0) {
      const temp = b;
      b = a % b;
      a = temp;
    }
    
    return a;
  }

  /**
   * Assess risk level for an arbitrage opportunity
   * @param {number} margin - Arbitrage margin percentage
   * @param {number} totalStake - Total stake amount
   * @param {Array} bookmakerCount - Number of bookmakers
   * @returns {Object} Risk assessment result
   */
  static assessRisk(margin, totalStake, bookmakerCount = 2) {
    try {
      const marginNum = parseFloat(margin);
      const stakeNum = parseFloat(totalStake);
      const bookmakerNum = parseInt(bookmakerCount, 10);
      
      if (isNaN(marginNum) || isNaN(stakeNum) || isNaN(bookmakerNum)) {
        return {
          isValid: false,
          riskLevel: RISK_LEVELS.VERY_HIGH,
          riskScore: 100,
          riskFactors: ['Invalid input parameters'],
          error: 'Invalid input parameters'
        };
      }
      
      let riskScore = 0;
      const riskFactors = [];
      
      // Margin-based risk (higher margin = lower risk)
      if (marginNum < 0.5) {
        riskScore += 40;
        riskFactors.push('Very low margin (< 0.5%)');
      } else if (marginNum < 1) {
        riskScore += 25;
        riskFactors.push('Low margin (< 1%)');
      } else if (marginNum < 2) {
        riskScore += 10;
        riskFactors.push('Moderate margin (< 2%)');
      } else {
        riskFactors.push('Good margin (>= 2%)');
      }
      
      // Stake-based risk (very high stakes = higher risk)
      if (stakeNum > 10000) {
        riskScore += 30;
        riskFactors.push('Very high stake amount (> 10000)');
      } else if (stakeNum > 5000) {
        riskScore += 15;
        riskFactors.push('High stake amount (> 5000)');
      } else if (stakeNum < 100) {
        riskScore += 5;
        riskFactors.push('Low stake amount (< 100)');
      }
      
      // Bookmaker count risk (fewer bookmakers = higher risk)
      if (bookmakerNum < 2) {
        riskScore += 30;
        riskFactors.push('Too few bookmakers (< 2)');
      } else if (bookmakerNum === 2) {
        riskScore += 10;
        riskFactors.push('Only 2 bookmakers');
      } else {
        riskFactors.push('Multiple bookmakers (>= 3)');
      }
      
      // Determine risk level
      let riskLevel;
      if (riskScore >= 70) {
        riskLevel = RISK_LEVELS.VERY_HIGH;
      } else if (riskScore >= 50) {
        riskLevel = RISK_LEVELS.HIGH;
      } else if (riskScore >= 30) {
        riskLevel = RISK_LEVELS.MEDIUM;
      } else if (riskScore >= 15) {
        riskLevel = RISK_LEVELS.LOW;
      } else {
        riskLevel = RISK_LEVELS.VERY_LOW;
      }
      
      return {
        isValid: true,
        riskLevel,
        riskScore: Math.min(100, riskScore),
        riskFactors,
        recommendation: this.getRiskRecommendation(riskLevel),
        error: null
      };
    } catch (error) {
      return {
        isValid: false,
        riskLevel: RISK_LEVELS.VERY_HIGH,
        riskScore: 100,
        riskFactors: [`Error: ${error.message}`],
        error: `Risk assessment error: ${error.message}`
      };
    }
  }

  /**
   * Get risk recommendation based on risk level
   * @param {string} riskLevel - Risk level constant
   * @returns {string} Recommendation message
   */
  static getRiskRecommendation(riskLevel) {
    const recommendations = {
      [RISK_LEVELS.VERY_LOW]: 'Very safe arbitrage opportunity',
      [RISK_LEVELS.LOW]: 'Safe arbitrage opportunity',
      [RISK_LEVELS.MEDIUM]: 'Moderate risk - proceed with caution',
      [RISK_LEVELS.HIGH]: 'High risk - consider reducing stake amount',
      [RISK_LEVELS.VERY_HIGH]: 'Very high risk - not recommended'
    };
    
    return recommendations[riskLevel] || 'Unknown risk level';
  }

  /**
   * Analyze betting scenario
   * @param {Array} odds - Array of odds
   * @param {number} totalStake - Total stake amount
   * @param {Object} options - Analysis options
   * @returns {Object} Complete scenario analysis
   */
  static analyzeScenario(odds, totalStake, options = {}) {
    try {
      // Calculate margin
      const marginResult = this.calculateMargin(
        odds.map(odd => typeof odd === 'object' ? odd.value : odd)
      );
      
      // Calculate stake distribution
      const distributionResult = this.calculateStakeDistribution(odds, totalStake, options);
      
      // Calculate profit scenarios
      const profitScenarios = distributionResult.isValid 
        ? this.calculateProfitScenarios(distributionResult.distribution, totalStake)
        : [];
      
      // Assess risk
      const riskAssessment = this.assessRisk(
        marginResult.marginPercentage,
        totalStake,
        odds.length
      );
      
      return {
        isValid: marginResult.isValid && distributionResult.isValid,
        margin: marginResult,
        distribution: distributionResult,
        profitScenarios,
        riskAssessment,
        summary: {
          totalOutcomes: odds.length,
          totalStake: parseFloat(totalStake),
          guaranteedProfit: distributionResult.guaranteedProfit,
          guaranteedProfitPercentage: distributionResult.guaranteedProfitPercentage,
          arbitrageMargin: marginResult.marginPercentage,
          riskLevel: riskAssessment.riskLevel,
          recommendation: riskAssessment.recommendation
        },
        error: marginResult.error || distributionResult.error || null
      };
    } catch (error) {
      return {
        isValid: false,
        margin: null,
        distribution: null,
        profitScenarios: [],
        riskAssessment: null,
        summary: null,
        error: `Scenario analysis error: ${error.message}`
      };
    }
  }

  /**
   * Round number to specified precision
   * @param {number} value - Value to round
   * @param {number} precision - Number of decimal places
   * @returns {number} Rounded value
   */
  static roundToPrecision(value, precision = 2) {
    try {
      const num = parseFloat(value);
      if (isNaN(num)) {
        return 0;
      }
      
      const factor = Math.pow(10, precision);
      return Math.round(num * factor) / factor;
    } catch (error) {
      console.error('Error rounding value:', error);
      return 0;
    }
  }
}

// Export the BusinessLogic class
// Note: BETTING_LIMITS, ODDS_FORMAT, and RISK_LEVELS are already exported above
export default BusinessLogic;

